import { DefaultMetricOptions, Goal } from '@griply/types';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { getSubGoals } from './goals/get-sub-goals';

export type GetGoalCompletionOptions = {
  goals: Goal[];
};

export type GoalCompletion = {
  start: number;
  current: number;
  end: number;
};

export const getGoalCompletion = (
  goal: Goal,
  { goals }: GetGoalCompletionOptions,
): GoalCompletion => {
  if (!goal.metric?.id) {
    return {
      start: 0,
      current: 0,
      end: Infinity,
    };
  }

  const subGoals = getSubGoals(goal.id, { goals, recursive: true });

  if (goal.metric?.id === DefaultMetricOptions.CompletedTasks) {
    return {
      start: 0,
      current: subGoals.reduce(
        (acc, { completedTaskCount }) => acc + (completedTaskCount ?? 0),
        goal.completedTaskCount ?? 0,
      ),
      end: subGoals.reduce(
        (acc, { taskCount }) => acc + (taskCount ?? 0),
        goal.taskCount ?? 0,
      ),
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.SubGoalProgress) {
    return {
      start: 0,
      current: subGoals.filter(({ completedAt }) => !!completedAt).length,
      end: subGoals.length,
    };
  }

  const start = goal.metric?.startValue ?? 0;
  const lastEntry = goal.metric.entries
    ?.slice()
    .sort((entryA, entryB) => entryB.date.getTime() - entryA.date.getTime())[0];
  const current = lastEntry
    ? calculateMetricEntryValue(lastEntry.id, goal.metric.entries!, start)
    : start;

  return {
    start,
    end: goal.metric?.targetValue ?? Infinity,
    current,
  };
};
